import { Entity } from '@backstage/catalog-model';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * A custom hook for managing entity-related controls such as unregister and inspect dialogs.
 *
 * This hook provides functionality to:
 * - Open and close the unregister dialog.
 * - Confirm the unregistration of an entity and navigate back to the catalog.
 * - Open and close the inspect dialog.
 *
 * @param entity - The entity to manage controls for.
 * @returns An object containing state and handlers for entity-related dialogs and actions.
 *
 * @category Entity Menu
 */
export const useEntityControl = (entity: Entity) => {
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const navigate = useNavigate();

  /**
   * State indicating whether the unregister dialog is open.
   */
  const [unregisterDialogOpen, setUnregisterDialogOpen] = React.useState(false);

  /**
   * State indicating whether the inspect dialog is open.
   */
  const [inspectDialogOpen, setInspectDialogOpen] = React.useState(false);

  /**
   * Closes the unregister dialog.
   */
  const handleUnregisterClose = () => setUnregisterDialogOpen(false);

  /**
   * Confirms the unregistration of the entity.
   *
   * If successful, navigates back to the catalog. If an error occurs, an alert is displayed.
   */
  const handleUnregisterConfirm = async () => {
    try {
      await catalogApi.removeEntityByUid(entity.metadata.name);
      setUnregisterDialogOpen(false);
      navigate('/catalog');
    } catch (error) {
      alertApi.post({
        display: 'transient',
        message: 'Error unregistering entity!',
        severity: 'error',
      });
    }
  };

  /**
   * Closes the inspect dialog.
   */
  const handleInspectClose = () => setInspectDialogOpen(false);

  return {
    unregisterDialogOpen,
    setUnregisterDialogOpen,
    handleUnregisterClose,
    handleUnregisterConfirm,
    inspectDialogOpen,
    setInspectDialogOpen,
    handleInspectClose,
  };
};
